import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Grid,
  MenuItem,
  Checkbox,
  ListItemText,
  Switch,
  Button,
  IconButton,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import GroupIcon from "@mui/icons-material/Group";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PaidIcon from "@mui/icons-material/Paid";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PercentIcon from "@mui/icons-material/Percent";
import SaveIcon from "@mui/icons-material/Save";

import ApiServices from "../../../Services/ApiServices/ApiServices";

/* ---------------- STAT CARD ---------------- */
const StatCard = ({ label, value, icon, accent }) => (
  <Paper
    sx={{
      height: 140,
      p: 2.5,
      borderRadius: 3,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      boxShadow: "0 12px 32px rgba(0,0,0,0.08)",
    }}
  >
    {/* TOP: Icon */}
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: "50%",
        bgcolor: accent,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
      }}
    >
      {icon}
    </Box>

    {/* MIDDLE: Label */}
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{
        mt: 1,
        lineHeight: 1.2,
        wordBreak: "break-word",
        display: "-webkit-box",
        WebkitLineClamp: { xs: 2, sm: 1 }, // wrap on mobile
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }}
    >
      {label}
    </Typography>

    {/* BOTTOM: Value */}
    <Typography
      variant="h5"
      fontWeight={800}
      sx={{
        mt: 1,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {value}
    </Typography>
  </Paper>
);

const ReferLeads = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [searchService, setSearchService] = useState("");
  const [selectedSources, setSelectedSources] = useState([]);
  const [convertedFilter, setConvertedFilter] = useState("ALL");
  const [paymentFilter, setPaymentFilter] = useState("ALL");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [jobValueDraft, setJobValueDraft] = useState({});
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const showSnack = (message, severity = "success") =>
    setSnack({ open: true, message, severity });

  /* ---------------- FETCH DATA ---------------- */
  const fetchLeads = async () => {
    try {
      setLoading(true);
      const res = await ApiServices.get("/referral-leads/");
      setLeads(Array.isArray(res?.data) ? res.data : []);
    } catch {
      showSnack("Failed to load referral leads", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await ApiServices.get("/referral-leads/stats/");
      setStats(res?.data || null);
    } catch {
      setStats(null);
    }
  };

  useEffect(() => {
    fetchLeads();
    fetchStats();
  }, []);

  /* ---------------- PATCH ACTIONS ---------------- */
  const toggleConverted = async (lead) => {
    try {
      await ApiServices.patch(`/referral-leads/${lead.id}/`, {
        is_converted: !lead.is_converted,
      });
      showSnack("Lead conversion updated");
      fetchLeads();
      fetchStats();
    } catch {
      showSnack("Failed to update conversion", "error");
    }
  };

  const saveJobValue = async (lead) => {
    try {
      await ApiServices.patch(`/referral-leads/${lead.id}/convert/`, {
        job_value: Number(jobValueDraft[lead.id]),
      });
      showSnack("Job value updated");
      fetchLeads();
      fetchStats();
    } catch {
      showSnack("Failed to update job value", "error");
    }
  };

  const updatePaymentStatus = async (lead, status) => {
    try {
      await ApiServices.patch(`/referral-leads/${lead.id}/payment/`, {
        payment_status: status,
      });
      showSnack("Payment status updated");
      fetchLeads();
      fetchStats();
    } catch {
      showSnack("Failed to update payment status", "error");
    }
  };

  /* ---------------- RESET FILTERS ---------------- */
  const resetFilters = () => {
    setSearchService("");
    setSelectedSources([]);
    setConvertedFilter("ALL");
    setPaymentFilter("ALL");
    setFromDate("");
    setToDate("");
  };

  /* ---------------- UNIQUE SOURCES ---------------- */
  const referralSources = useMemo(() => {
    const map = new Map();
    leads.forEach((l) => map.set(l.referral_source, l.referral_source_name));
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [leads]);

  /* ---------------- FILTER LOGIC ---------------- */
  const filteredLeads = useMemo(() => {
    return leads.filter((l) => {
      if (searchService && !l.service_needed?.toLowerCase().includes(searchService.toLowerCase()))
        return false;
      if (selectedSources.length && !selectedSources.includes(l.referral_source))
        return false;
      if (convertedFilter !== "ALL" && String(l.is_converted) !== convertedFilter)
        return false;
      if (paymentFilter !== "ALL" && l.payment_status !== paymentFilter)
        return false;
      if (fromDate && new Date(l.created_at) < new Date(fromDate)) return false;
      if (toDate && new Date(l.created_at) > new Date(toDate)) return false;
      return true;
    });
  }, [
    leads,
    searchService,
    selectedSources,
    convertedFilter,
    paymentFilter,
    fromDate,
    toDate,
  ]);

  return (
    <Box p={{ xs: 2, md: 4 }}>
      {/* ================= STATS ================= */}
{stats && (
  <Box sx={{ mb: 4 }}>
    <Grid
      container
      spacing={3}
      justifyContent="center"
      alignItems="stretch"
    >
      {/* CARD 1 */}
      <Grid
        item
        xs={6}      // mobile: 2 cards
        sm={6}      // tablet: 2 cards
        md={3}      // desktop: 4 cards
        display="flex"
      >
        <StatCard
          label="Total Leads"
          value={stats.total_leads}
          icon={<GroupIcon />}
          accent="#1976d2"
        />
      </Grid>

      {/* CARD 2 */}
      <Grid item xs={6} sm={6} md={3} display="flex">
        <StatCard
          label="Converted Leads"
          value={stats.converted_leads}
          icon={<TrendingUpIcon />}
          accent="#2e7d32"
        />
      </Grid>

      {/* CARD 3 */}
      <Grid item xs={6} sm={6} md={3} display="flex">
        <StatCard
          label="Conversion Rate"
          value={stats.conversion_rate}
          icon={<PercentIcon />}
          accent="#0288d1"
        />
      </Grid>

      {/* CARD 4 */}
      <Grid item xs={6} sm={6} md={3} display="flex">
        <StatCard
          label="Total Commission"
          value={`$${stats.total_commission_value}`}
          icon={<PaidIcon />}
          accent="#6a1b9a"
        />
      </Grid>

      {/* CARD 5 */}
      <Grid item xs={6} sm={6} md={3} display="flex">
        <StatCard
          label="Paid Commissions"
          value={stats.paid_commissions}
          icon={<PaidIcon />}
          accent="#2e7d32"
        />
      </Grid>

      {/* CARD 6 */}
      <Grid item xs={6} sm={6} md={3} display="flex">
        <StatCard
          label="Unpaid Commissions"
          value={stats.unpaid_commissions}
          icon={<PendingActionsIcon />}
          accent="#ed6c02"
        />
      </Grid>

      {/* CARD 7 */}
      <Grid item xs={6} sm={6} md={3} display="flex">
        <StatCard
          label="Paid Value"
          value={`$${stats.total_paid_value}`}
          icon={<PaidIcon />}
          accent="#2e7d32"
        />
      </Grid>

      {/* CARD 8 */}
      <Grid item xs={6} sm={6} md={3} display="flex">
        <StatCard
          label="Pending Value"
          value={`$${stats.total_pending_value}`}
          icon={<PendingActionsIcon />}
          accent="#d32f2f"
        />
      </Grid>
    </Grid>
  </Box>
)}


      {/* ================= FILTERS ================= */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="medium"
              label="Search Service"
              helperText="Filter by service name"
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
              value={searchService}
              onChange={(e) => setSearchService(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="medium"
              label="Referral Source"
              helperText="Select referral source(s)"
              SelectProps={{
                multiple: true,
                renderValue: (selected) =>
                  referralSources
                    .filter((s) => selected.includes(s.id))
                    .map((s) => s.name)
                    .join(", "),
              }}
              value={selectedSources}
              onChange={(e) => setSelectedSources(e.target.value)}
            >
              {referralSources.map((src) => (
                <MenuItem key={src.id} value={src.id}>
                  <Checkbox checked={selectedSources.includes(src.id)} />
                  <ListItemText primary={src.name} />
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="medium"
              label="Converted"
              helperText="Converted to job?"
              value={convertedFilter}
              onChange={(e) => setConvertedFilter(e.target.value)}
            >
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="true">Converted</MenuItem>
              <MenuItem value="false">Not Converted</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="medium"
              label="Payment Status"
              helperText="Paid / Unpaid"
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
            >
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="PAID">Paid</MenuItem>
              <MenuItem value="NOT_PAID">Not Paid</MenuItem>
              <MenuItem value="PARTIAL">Partial</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={1}>
            <TextField
              type="date"
              fullWidth
              size="medium"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={1}>
            <TextField
              type="date"
              fullWidth
              size="medium"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={12} textAlign="right">
            <Button startIcon={<RestartAltIcon />} onClick={resetFilters}>
              Reset Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* ================= TABLE ================= */}
      <Paper sx={{ p: 3 }}>
        {loading ? (
          <Box textAlign="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Source</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Job Value</TableCell>
                  <TableCell>Converted</TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredLeads.map((l) => (
                  <TableRow key={l.id} hover>
                    <TableCell>{l.referral_source_name}</TableCell>
                    <TableCell>{l.customer_name}</TableCell>
                    <TableCell>{l.customer_phone}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{l.service_needed}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>{l.notes}</TableCell>

                    <TableCell>
                      <Box display="flex" gap={1}>
                        <TextField
                          size="small"
                          type="number"
                          value={jobValueDraft[l.id] ?? l.job_value ?? ""}
                          onChange={(e) =>
                            setJobValueDraft({
                              ...jobValueDraft,
                              [l.id]: e.target.value,
                            })
                          }
                        />
                        <IconButton onClick={() => saveJobValue(l)}>
                          <SaveIcon />
                        </IconButton>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Switch
                        checked={l.is_converted}
                        onChange={() => toggleConverted(l)}
                        color="success"
                      />
                    </TableCell>

                    <TableCell>
                      <TextField
                        select
                        size="small"
                        value={l.payment_status}
                        onChange={(e) =>
                          updatePaymentStatus(l, e.target.value)
                        }
                      >
                        <MenuItem value="PAID">PAID</MenuItem>
                        <MenuItem value="NOT_PAID">NOT PAID</MenuItem>
                        <MenuItem value="PARTIAL">PARTIAL</MenuItem>
                      </TextField>
                    </TableCell>

                    <TableCell>
                      {new Date(l.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity} variant="filled">
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReferLeads;
